import { useState, useEffect } from 'react';
import { api, getUserId } from '@/lib/api';
import { User, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

export default function NicknameBox() {
  const [nickname, setNickname] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const userId = getUserId();

  useEffect(() => {
    const stored = localStorage.getItem('quiz_nickname');
    if (stored) {
      setNickname(stored);
      setInputValue(stored);
    }
  }, []);

  const handleSave = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      toast.error('Bitte gib einen Nickname ein');
      return;
    }
    
    setIsSaving(true);
    try {
      const res = await api.post('/user/nickname', { userId, nickname: trimmed.slice(0, 20) });
      const savedNickname = res.data.nickname;
      setNickname(savedNickname);
      setInputValue(savedNickname);
      localStorage.setItem('quiz_nickname', savedNickname);
      setIsExpanded(false);
      if (savedNickname === 'Na Na Na!') {
        toast.warning('Nickname war nicht zulässig');
      } else {
        toast.success('Gespeichert!');
      }
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    else if (e.key === 'Escape') setIsExpanded(false);
  };

  // Compact display when nickname is set
  if (nickname && !isExpanded) {
    return (
      <button 
        onClick={() => setIsExpanded(true)}
        className="w-full flex items-center justify-between px-3 py-2 mb-3 sm:mb-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors text-left"
        data-testid="nickname-box"
      >
        <div className="flex items-center gap-2">
          <User className="w-3.5 h-3.5 text-[#60A5FA]" />
          <span className="text-xs sm:text-sm text-[#9CA3AF]">Leaderboard-Name:</span>
          <span className="text-xs sm:text-sm text-white font-medium">{nickname}</span>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-[#4B5563]" />
      </button>
    );
  }

  // Collapsed prompt when no nickname
  if (!nickname && !isExpanded) {
    return (
      <button 
        onClick={() => setIsExpanded(true)}
        className="w-full flex items-center justify-between px-3 py-2 mb-3 sm:mb-4 rounded-lg bg-[#3B82F6]/10 hover:bg-[#3B82F6]/15 border border-[#3B82F6]/20 transition-colors text-left"
        data-testid="nickname-box"
      >
        <div className="flex items-center gap-2">
          <User className="w-3.5 h-3.5 text-[#60A5FA]" />
          <span className="text-xs sm:text-sm text-[#60A5FA]">Namen fürs Leaderboard setzen?</span>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-[#60A5FA]" />
      </button>
    );
  }

  // Expanded input form
  return (
    <div className="mb-3 sm:mb-4 rounded-lg bg-white/5 border border-white/10 overflow-hidden" data-testid="nickname-box">
      <button 
        onClick={() => setIsExpanded(false)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <User className="w-3.5 h-3.5 text-[#60A5FA]" />
          <span className="text-xs sm:text-sm text-white font-medium">Leaderboard-Name</span>
        </div>
        <ChevronUp className="w-3.5 h-3.5 text-[#9CA3AF]" />
      </button>
      <div className="px-3 pb-3 flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.slice(0, 20))}
          onKeyDown={handleKeyDown}
          placeholder="z.B. CodeMaster42"
          className="flex-1 bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 text-sm text-white placeholder-[#4B5563] focus:border-[#3B82F6] focus:outline-none"
          data-testid="nickname-input"
          autoFocus
        />
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-3 py-1.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-md text-sm transition-colors disabled:opacity-50 flex items-center gap-1"
          data-testid="nickname-save-btn"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
